import { storeToRefs, defineStore } from 'pinia';
import { useJobsStore } from '@/stores/useJobs';
import { useMixPanelStore } from '@/stores/useMixPanelStore';
import searchJobsRules from '@/constants/searchJobsRules';
import { isAbortError } from '@/utilities/isAbortError';

const { addRumError, addRumAction } = useAcvDatadog();

export const defineDefaultDistanceState = (type, id, postCode, distance) => {
  const defaultPostalCode = JSON.parse(sessionStorage.getItem('postalCode'));

  return {
    postCode: postCode ? postCode : defaultPostalCode,
    distance: distance ? distance : 0,
    type: type,
    id: id ? id : `${type}-${Date.now()}`, // ensures a unique id even after reloading
    filterType: 'distances',
  };
};

export const useFilterOptions = (id, filterAPI, options, savedSearchState) => {
  return defineStore(id, {
    state: () => {
      return {
        location: {
          pickup: {
            distances: [defineDefaultDistanceState('pickup')],
            states: [],
          },

          delivery: {
            distances: [defineDefaultDistanceState('delivery')],
            states: [],
          },
        },
        search: [],
        jobTypes: [],
        sort: options.sortObj ? options.sortObj : {},
        previousSort: {
          text: null,
          value: null,
          order: null,
        },
        filters: {
          deliveryPastDue: false,
          pickupPastDue: false,
          deliverToday: false,
          pickupToday: false,
          bolNeeded: false,
        },
        ...savedSearchState,
      };
    },
    actions: {
      resetAllFilters() {
        this.$reset();
        this.updateMixpanelProperties('clear all filters clicked');
      },
      updateFilters(name, value) {
        this.filters = { ...this.filters, [name]: value };
      },
      updateBulkFilters(filters) {
        this.filters = filters;
      },
      pushNewDistanceRow(type) {
        this.$patch((state) => {
          state.location[type].distances.push(defineDefaultDistanceState(type));
        });
      },
      removeDistanceRow(type, rowIndex) {
        if (rowIndex >= 0) {
          this.location[type].distances.splice(rowIndex, 1);
        }
      },
      resetFiltersByType(type) {
        this.location[type].distances = [];
        this.location[type].states = [];
        this.updateMixpanelProperties(`clear all ${type} filters clicked`);
      },
      setDistanceRow(type, filterType, id, formData, $index) {
        this.location[type][filterType][$index] = defineDefaultDistanceState(
          type,
          id,
          formData.postCode,
          formData.distance,
          $index,
        );

        this.updateMixpanelProperties(`${type} distance jobs filtered`);
      },
      removeAppliedDistanceFilter(type, id) {
        // remove applied filter from array
        this.location[type].distances = this.location[type].distances.filter(
          (item) => item.id !== id,
        );

        // if removing the last applied filter, reset filters to default
        if (this.location[type].distances.length === 0) {
          this.location[type].distances = [defineDefaultDistanceState(type)];
        }

        this.updateMixpanelProperties(`remove ${type} distance jobs filter`);
      },
      removeAppliedStateFilter(type, id) {
        const stateFilters = this.location[type].states;

        this.location[type].states = stateFilters.filter((item) => item.id !== id);
        this.updateMixpanelProperties(`remove ${type} state jobs filter`);
      },
      pushNewSearchQuery(query) {
        // DEV NOTE: When the search feature was originally developed we were intending to pass multiple values.  For MVP we are limiting scope to a single search at a time.
        this.search = [query];

        this.updateMixpanelProperties('jobs searched');
      },
      removeAppliedSearchFilter() {
        this.search = [];
        this.updateMixpanelProperties('jobs searched');
      },
      setStateFilters(type, savedStates) {
        this.location[type].states = savedStates;

        // type === picked up || delivered
        this.updateMixpanelProperties(`${type} state jobs filtered`);
      },
      setJobTypeFilters(selectedJobTypes) {
        this.jobTypes = selectedJobTypes;
        this.updateMixpanelProperties(`job types filtered`);
      },
      updateSortParams(sortObj) {
        if (sortObj) {
          this.previousSort = this.sort;
          this.sort = sortObj;
          this.updateMixpanelProperties('job sorted');
        }
      },
      clearJobTypeFilters() {
        this.jobTypes = [];
      },
      removeAppliedJobFilter(id) {
        const jobFilters = this.jobTypes;
        this.jobTypes = jobFilters.filter((item) => item.id !== id);
      },
      async fetchFilteredJobs(style) {
        const { $api, $cancelAllRequests } = useNuxtApp();
        $cancelAllRequests('New filter criteria applied');

        const jobsStore = useJobsStore();
        const { jobs, pagination, isPending, currentPageIndex, rowsPerPage } =
          storeToRefs(jobsStore);

        const body = {
          location: {
            pickup: {
              distances: this.appliedPickupDistanceFilters,
              states: this.appliedPickupStateFilters,
            },
            delivery: {
              distances: this.appliedDeliveryDistanceFilters,
              states: this.appliedDeliveryStateFilters,
            },
          },
          search: this.search?.length > 0 ? this.search : null,
          jobTypes: this.appliedJobTypeFilters,
          ...this.filters,
        };

        const params = {
          page: currentPageIndex.value,
          size: rowsPerPage.value,
          sort: `${[this.sort.value, this.sort.order]}`,
        };

        const ddContext = {
          style,
          filterAPI,
          body,
          params,
        };

        isPending.value = true;

        try {
          const response = await $api(filterAPI, {
            method: 'POST',
            body,
            params,
          });

          if (!response?.data?.content) {
            addRumAction('Fetch filtered jobs return no results', ddContext);
          }

          const newJobs = response.data.content;

          jobs.value = style === 'append' ? [...jobs.value, ...newJobs] : newJobs;

          pagination.value = response.data.pagination;
        } catch (error) {
          if (isAbortError(error)) {
            addRumAction('Fetch filtered jobs aborted', {
              ...ddContext,
              reason: error.cause || error.message,
            });
          } else {
            addRumError(error, {
              ...ddContext,
              actionName: 'Failed to fetch filtered jobs',
              currentJobsCount: jobs.value?.length,
              pageIndex: currentPageIndex.value,
            });
          }
        } finally {
          isPending.value = false;
        }
      },
      setToFirstPage() {
        const jobsStore = useJobsStore();
        const { jobs, currentPageIndex } = storeToRefs(jobsStore);

        jobs.value = jobs.value.slice(0, 50);
        currentPageIndex.value = 0;
      },
      updateMixpanelProperties(eventName) {
        if (!savedSearchState) {
          const mixPanelStore = useMixPanelStore();
          mixPanelStore.setSearchText(this.searchText);
          mixPanelStore.setSortOptions({
            previousSortByDirection: this.previousSort.order,
            previousSortByField: this.previousSort.text,
            sortByDirection: this.sort.order,
            sortByField: this.sort.text,
          });
          mixPanelStore.setFilterOptions({
            pickupStates: this.appliedPickupStateFilters.length
              ? this.appliedPickupStateFilters
              : null,
            deliveryStates: this.appliedDeliveryStateFilters.length
              ? this.appliedDeliveryStateFilters
              : null,
            pickupZipRanges: this.appliedPickupDistanceFilters.length
              ? this.appliedPickupDistanceFilters
              : null,
            deliveryZipRanges: this.appliedDeliveryDistanceFilters.length
              ? this.appliedDeliveryDistanceFilters
              : null,
            jobTypes: this.jobTypes.length ? this.jobTypes : null,
          });

          mixPanelStore.fetchMixPanel(eventName);
        }
      },
      resetBulkFiltersAndSort() {
        this.$patch({
          filters: {
            deliveryPastDue: false,
            pickupPastDue: false,
            deliverToday: false,
            pickupToday: false,
            bolNeeded: false,
          },
          sort: options.sortObj,
        });
      },

      // saved search actions:
      async getSavedSearchesForCarrier() {
        this.loading = true;
        this.errors = [];
        this.savedSearches = [];
        const { $api } = useNuxtApp();
        let response = null;
        try {
          response = await $api('/api/v1/saved-search');

          if (response?.data?.length > 0) {
            this.savedSearches = response.data;
          }
        } catch (error) {
          this.errors.push(error);
          // Check if it's an abort error (intentional cancellation)
          if (isAbortError(error)) {
            addRumAction(`Get saved searches: ${error.cause?.message || error.message}`);
          } else {
            addRumError(new Error('Failed to fetch saved searches'), {
              error_message: error.message,
              error_status: error.status || error.statusCode,
              error,
              loading: this.loading,
              search_count: this.savedSearches.length,
              active_filters: this.appliedFilters,
            });
          }
        } finally {
          this.loading = false;
        }
      },
      async saveSearch() {
        this.loading = true;
        this.errors = [];

        const body = {
          id: this.savedSearchEditID,
          searchName: this.createSavedSearchName,
          searchCriteria: {
            location: {
              pickup: {
                distances: this.appliedPickupDistanceFilters,
                states: this.appliedPickupStateFilters,
              },
              delivery: {
                distances: this.appliedDeliveryDistanceFilters,
                states: this.appliedDeliveryStateFilters,
              },
            },
            sort: `${this.sort.value},${this.sort.order}`,
            jobTypes: this.appliedJobTypeFilters,
          },
        };

        try {
          const { $api } = useNuxtApp();
          const response = await $api(`api/v1/saved-search/`, {
            method: 'POST',
            body,
          });

          if (response.data?.id) {
            // load saved searches after with new one
            await this.getSavedSearchesForCarrier();

            if (this.savedSearchEditID) {
              this.showSnackBarMessage('Your saved search has been updated.');
              this.savedSearchEditID = null;
            } else {
              this.showSnackBarMessage(
                `Your search has been saved as ${response.data?.searchName}`,
              );
            }

            useMixpanelTrack('lb2 saved search created', {
              saved_search_id: response.data?.id,
              ...body,
            });

            return response.data?.id;
          }
        } catch (error) {
          this.errors = this.errors;
        }

        // reset location data
        this.resetFormData();
        this.loading = false;
      },
      resetFormData() {
        this.createSavedSearchName = '';

        this.location = {
          pickup: {
            distances: [defineDefaultDistanceState('pickup')],
            states: [],
          },

          delivery: {
            distances: [defineDefaultDistanceState('delivery')],
            states: [],
          },
        };

        this.sort = {
          text: 'Dispatched Date',
          value: 'dateDispatched',
          order: 'desc',
        };

        this.jobTypes = [];

        // also reset the quick save status
        this.isQuickSave = false;
        this.savedSearchEditID = null;
      },
      async deleteSavedSearch(id) {
        try {
          const { $api } = useNuxtApp();
          this.loading = true;
          this.errors = [];

          await $api(`/api/v1/saved-search/${id}`, { method: 'DELETE' });

          // remove the deleted filter from the store
          this.savedSearches = this.savedSearches.filter((savedSearch) => savedSearch?.id !== id);

          this.showSnackBarMessage('Your saved search has been deleted.');

          if (this.activeSavedSearch.id === id) {
            this.activeSavedSearch = null;
          }
        } catch (error) {
          this.errors = this.errors;
        }

        this.loading = false;
      },
      setActiveSavedSearch(id) {
        this.activeSavedSearch = this.savedSearches.find((item) => item.id === id);
      },
      toggleSavedSearchFormWithSpecificFilters(id) {
        const savedSearch = this.savedSearches.find((item) => item.id === id);

        if (!savedSearch || !savedSearch.searchCriteria?.sort) {
          addRumAction('Saved search not found or invalid:', {
            selected_saved_search_id: id,
            saved_search: savedSearch,
          });
          return;
        }

        const [value, order] = savedSearch.searchCriteria.sort.split(',');

        const sortOption = searchJobsRules.DEFAULT.SORT_BY.options.find(
          (item) => item.value === value,
        );

        this.$patch({
          createSavedSearchName: savedSearch.searchName ?? '',
          location: savedSearch.searchCriteria.location ?? null,
          jobTypes: savedSearch.searchCriteria.jobTypes ?? [],
          sort: {
            order,
            value,
            text: sortOption?.text ?? '',
          },
        });

        this.activeSavedSearch = null;
        this.savedSearchEditID = id;
        this.isCreateFormOpen = true;
      },
      showSnackBarMessage(message) {
        this.snackBarMessage = message;
      },
      resetSnackBarMessage() {
        this.snackBarMessage = null;
      },
    },
    getters: {
      appliedDeliveryStateFilters: (state) => [...state.location.delivery.states],
      appliedPickupStateFilters: (state) => [...state.location.pickup.states],
      appliedPickupDistanceFilters: (state) => [
        ...state.location.pickup.distances.filter((row) => row.distance.length > 0), // filter out empty rows
      ],
      appliedDeliveryDistanceFilters: (state) => [
        ...state.location.delivery.distances.filter((row) => row.distance.length > 0), // filter out empty rows
      ],
      appliedDeliveryFilters: (state) => [
        ...state.appliedDeliveryDistanceFilters,
        ...state.appliedDeliveryStateFilters,
      ],
      appliedPickupFilters: (state) => [
        ...state.appliedPickupDistanceFilters,
        ...state.appliedPickupStateFilters,
      ],
      appliedFilters: (state) => [
        ...state.appliedPickupFilters,
        ...state.appliedDeliveryFilters,
        ...state.search,
        ...state.appliedJobTypeFilters,
      ],
      appliedBulkFilters: (state) => {
        const bulkFilterArray = [];
        for (const prop in state.filters) {
          if (state.filters[prop]) {
            bulkFilterArray.push(true);
          }
        }
        return bulkFilterArray;
      },
      searchText: (state) => (state.search[0]?.value ? state.search[0].value : null),
      subFilters: (state) => state.filters,
      appliedJobTypeFilters: (state) => [...state.jobTypes],
    },
  });
};
